import CloseIcon from "@mui/icons-material/Close";
import {
  Backdrop,
  Box,
  Fade,
  IconButton,
  Modal as MUIModal,
  Typography,
} from "@mui/material";

import styled from "@emotion/styled";
import { vibrant } from "@repo/dashboard-constants";

export type ModalProps = {
  title: string;
  children: React.ReactNode;
  open: boolean;
  restricted?: boolean; //If the user shouldn't be allowed to close the modal
  handleClose: () => void;
  size?: "sm" | "md" | "lg";
};

const getModalMaxWidth = (size?: "sm" | "md" | "lg") => {
  switch (size) {
    case "sm":
      return "600px";
    case "md":
      return "960px";
    case "lg":
      return "1280px";
    default:
      return "960px";
  }
};

const ModalBox = styled(Box)<{ size?: "sm" | "md" | "lg" }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  max-width: ${({ size }) => getModalMaxWidth(size)};
  max-height: 90vh;
  transform: translate(-50%, -50%);
  background: transparent;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  ul {
    gap: 8px !important;
  }
`;

const TitleBar = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  background: ${vibrant[900]};
  border-bottom: 1px solid ${vibrant[800]};
  border-radius: 8px 8px 0 0;
`;

const ModalBody = styled(Box)`
  overflow: auto;
  flex: 1;
  width: 100%;
  padding: 24px;
  background: ${vibrant[900]};
  border-radius: 0 0 8px 8px;
`;

export const Modal = ({
  title,
  children,
  handleClose,
  open,
  restricted,
  size,
}: ModalProps) => {
  const handleCloseModal = () => {
    if (!restricted) {
      handleClose();
    }
  };

  return (
    <MUIModal
      open={open}
      onClose={handleCloseModal}
      closeAfterTransition
      disableEscapeKeyDown={restricted}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
      components={{
        Backdrop: Backdrop,
      }}
      componentsProps={{
        backdrop: {
          timeout: 500,
          onClick: restricted ? undefined : handleCloseModal,
        },
      }}
    >
      <Fade in={open}>
        <ModalBox size={size}>
          <TitleBar>
            <Typography variant="h4" sx={{ fontWeight: 600, color: "white" }}>
              {title}
            </Typography>
            {!restricted && (
              <IconButton
                onClick={handleClose}
                sx={{
                  color: vibrant[100],
                  "&:hover": { color: "white", backgroundColor: vibrant[800] },
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </TitleBar>
          <ModalBody>{children}</ModalBody>
        </ModalBox>
      </Fade>
    </MUIModal>
  );
};

export default Modal;
